import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectCurrentUser, selectUserId, selectUsers } from '../components/login/loginSlice';
import { useAccountBadges } from '../hooks/useAccountBadges';
import { resolveBusinessAccountId } from '../utils/accountUtils';
import { useSocket } from '../components/chat/useSocket';

const AccountBadgesContext = createContext(null);

export function AccountBadgesProvider({ children }) {
    const users = useSelector(selectUsers);
    const currentUser = useSelector(selectCurrentUser);
    const userId = useSelector(selectUserId);
    const location = useLocation();
    const { socket } = useSocket();
    const refreshKey = location.pathname;
    const businessAccountId = useMemo(
        () => resolveBusinessAccountId({ users, currentUser, userId }),
        [users, currentUser, userId]
    );

    const badges = useAccountBadges(users, {
        enabled: users?.length > 0 || !!businessAccountId,
        refreshKey,
        businessAccountId,
    });

    useEffect(() => {
        if (!socket || !badges?.refresh) return undefined;
        const onNewMessage = (data) => {
            const biz = Number(businessAccountId) || 0;
            const toId = Number(data?.to_id) || 0;
            const clientId = Number(data?.client_id) || 0;
            const fromId = Number(data?.from_id) || 0;
            const tracked = new Set(
                Object.keys(badges.byUserId || {}).map((id) => Number(id)).filter(Boolean)
            );
            if (biz) tracked.add(biz);
            const hits =
                (toId && tracked.has(toId)) ||
                (clientId && tracked.has(clientId));
            if (!hits) return;
            const senderType = String(data?.sender_type || '');
            if (senderType === 'клиент' || senderType === 'сотрудник') return;
            if (biz && fromId === biz) return;
            if (fromId && tracked.has(fromId) && !tracked.has(toId)) return;
            console.log('[web-badge] newMessage → refresh', {
                toId,
                clientId,
                fromId,
                biz,
                path: location.pathname,
            });
            // Не сбрасываем бейдж, если чат только в пикере; полный refresh подтянет count
            badges.refresh();
        };
        socket.on('newMessage', onNewMessage);
        const onTaskUpdated = () => {
            badges.refresh();
        };
        socket.on('taskUpdated', onTaskUpdated);
        return () => {
            socket.off('newMessage', onNewMessage);
            socket.off('taskUpdated', onTaskUpdated);
        };
    }, [socket, badges, businessAccountId, location.pathname]);

    return (
        <AccountBadgesContext.Provider value={badges}>
            {children}
        </AccountBadgesContext.Provider>
    );
}

export function useAccountBadgesContext() {
    const ctx = useContext(AccountBadgesContext);
    if (!ctx) {
        return {
            byUserId: {},
            byCompanyId: {},
            tasksByCompanyId: {},
            chatByCompanyId: {},
            chatByChatId: {},
            totals: { tasks: 0, documents: 0, chat: 0 },
            hasEmptyBankPack: false,
            loading: false,
            refresh: () => {},
            patchChatBadge: () => {},
        };
    }
    return ctx;
}

/** Счётчики по user_id для карточек пикера: tasks | documents | chat */
export function usePickerBadgeMap(section, companyUsers = null) {
    const { byUserId, byCompanyId, tasksByCompanyId, chatByCompanyId } = useAccountBadgesContext();

    return useMemo(() => {
        const map = {};
        Object.entries(byUserId || {}).forEach(([userId, row]) => {
            const id = Number(userId);
            if (!id) return;
            if (section === 'tasks') map[id] = row.tasks;
            else if (section === 'documents') map[id] = row.documents;
            else if (section === 'chat') map[id] = row.chat;
        });
        (companyUsers || []).forEach((u) => {
            const cardId = Number(u?.user_id);
            if (!cardId) return;
            const companyId = Number(u?.company_id) || 0;
            if (section === 'documents') {
                // Не копируем сумму по аккаунту на все карточки — только счётчик компании
                map[cardId] = companyId ? Number(byCompanyId?.[companyId]) || 0 : 0;
                return;
            }
            if (section === 'tasks') {
                map[cardId] = companyId ? Number(tasksByCompanyId?.[companyId]) || 0 : 0;
                return;
            }
            if (section === 'chat') {
                if (companyId) {
                    map[cardId] = Number(chatByCompanyId?.[companyId]) || 0;
                    return;
                }
                const legacy = Number(u?.legacy_user_id) || 0;
                map[cardId] = (legacy && map[legacy]) || map[cardId] || 0;
            }
        });
        return map;
    }, [byUserId, byCompanyId, tasksByCompanyId, chatByCompanyId, section, companyUsers]);
}
